const SHUTTER_SRC = "/instamatic.mp3";

let audioCtx = null;
let decodedBuffer = null;
let decodePromise = null;

let htmlAudio = null;

function getAudioContext() {
  if (typeof window === "undefined") return null;
  const Ctor = window.AudioContext || window.webkitAudioContext;
  if (!Ctor) return null;
  if (!audioCtx) audioCtx = new Ctor();
  return audioCtx;
}

function getHtmlAudio() {
  if (typeof window === "undefined") return null;
  if (!htmlAudio) {
    htmlAudio = new Audio(SHUTTER_SRC);
    htmlAudio.preload = "auto";
    htmlAudio.playsInline = true;
    htmlAudio.volume = 0.95;
  }
  return htmlAudio;
}

function startDecode() {
  if (decodePromise) return decodePromise;
  const ctx = getAudioContext();
  if (!ctx) {
    decodePromise = Promise.resolve(null);
    return decodePromise;
  }
  decodePromise = fetch(SHUTTER_SRC)
    .then((r) => r.arrayBuffer())
    .then((ab) => ctx.decodeAudioData(ab.slice(0)))
    .then((buf) => {
      decodedBuffer = buf;
      return buf;
    })
    .catch(() => {
      decodePromise = null;
      return null;
    });
  return decodePromise;
}

/**
 * Warm decode + try to unlock AudioContext (call from a user gesture, e.g. pointerdown on shutter).
 */
export function primeShutterAudio() {
  const ctx = getAudioContext();
  if (ctx?.state === "suspended") {
    void ctx.resume().catch(() => {});
  }
  void startDecode();
}

/**
 * Start loading/decoding as soon as the app mounts (no gesture required for decode).
 */
export function prefetchShutterAudio() {
  void startDecode();
}

function playDecodedBuffer(ctx) {
  if (!decodedBuffer) return;
  const src = ctx.createBufferSource();
  src.buffer = decodedBuffer;
  const gain = ctx.createGain();
  gain.gain.value = 0.95;
  src.connect(gain).connect(ctx.destination);
  src.start(0);
}

async function playWithWebAudio() {
  const ctx = getAudioContext();
  if (!ctx) return false;

  await startDecode();
  if (!decodedBuffer) return false;

  if (ctx.state === "suspended") {
    await ctx.resume();
  }

  playDecodedBuffer(ctx);
  return true;
}

function playWithHtmlAudio() {
  try {
    const audio = getHtmlAudio();
    if (!audio) return;
    audio.currentTime = 0;
    void audio.play();
  } catch {
    // ignore
  }
}

function getShutterDurationMs() {
  if (decodedBuffer?.duration) {
    return Math.max(120, Math.round(decodedBuffer.duration * 1000));
  }

  const audio = getHtmlAudio();
  if (audio && Number.isFinite(audio.duration) && audio.duration > 0) {
    return Math.max(120, Math.round(audio.duration * 1000));
  }

  // Conservative fallback if metadata/decode is not ready yet.
  return 280;
}

/**
 * Low-latency shutter when buffer is ready; falls back to <audio> otherwise.
 */
export async function playShutterSound() {
  try {
    const durationMs = getShutterDurationMs();
    const ctx = getAudioContext();
    // Same-tick playback when decode + context are already warm (common after first shot).
    if (ctx && decodedBuffer) {
      if (ctx.state === "running") {
        playDecodedBuffer(ctx);
        return durationMs;
      }
      if (ctx.state === "suspended") {
        await ctx.resume();
        playDecodedBuffer(ctx);
        return durationMs;
      }
    }

    const ok = await playWithWebAudio();
    if (!ok) playWithHtmlAudio();
    return durationMs;
  } catch {
    playWithHtmlAudio();
    return 280;
  }
}
